import { useBlocker, useRouter } from "@tanstack/solid-router";
import { onCleanup } from "solid-js";

/**
 * Back-button-closes-overlay (premium PWA/mobile pattern).
 *
 * When an overlay (non-routed dialog/sheet/command-palette/drawer) is open
 * and the user presses the browser's "back", we want to CLOSE the overlay instead of
 * navigating in the history.
 *
 * IMPORTANT — why we do NOT manipulate the history by hand: TanStack Router patches
 * the History API (on the `window.history` instance) and, besides keeping an
 * index/key per entry, fires `router.load()` on every new entry. Pushing
 * an entry by hand:
 *   - if it duplicates the router's index → `delta = 0` in the popstate → loop → HANGS;
 *   - if it increments it → the router treats it as a real navigation, reloads the route and
 *     REMOUNTS the component, resetting the overlay's local signal → the overlay does NOT OPEN.
 * That is why we use the router's own mechanism: `useBlocker`. We do not touch the
 * history on open (the overlay opens normally); we only intercept the "BACK" action:
 * we close the top overlay and cancel the navigation (the router does `go(1)`).
 *
 * LIFO: several nested overlays register their entry; "back" closes the most recent
 * one still open. It does not interfere with real navigation (PUSH/REPLACE/FORWARD pass).
 */

interface OverlayEntry {
  isOpen: () => boolean;
  close: () => void;
}

const stack: OverlayEntry[] = [];

/**
 * Pure, testable logic: given a navigation action, it decides whether to CLOSE the
 * overlay (and return `true` to BLOCK the navigation) instead of navigating.
 * It only intercepts "BACK", and only if `entry` is the most recently opened overlay.
 */
export function resolveBackDismiss(
  action: string,
  entry: OverlayEntry,
  overlays: readonly OverlayEntry[] = stack,
): boolean {
  if (action !== "BACK") {
    return false;
  }
  const topmostOpen = overlays.findLast((candidate) => candidate.isOpen());
  if (topmostOpen === entry) {
    entry.close();
    return true;
  }
  return false;
}

/**
 * @param isOpen  reactive signal: `true` while the overlay is open.
 * @param close   closes the overlay (e.g. `() => onOpenChange(false)`).
 */
export function useBackDismiss(isOpen: () => boolean, close: () => void): void {
  // Without RouterProvider (isolated component tests / SSR) the blocker does not apply;
  // back-dismiss is a progressive enhancement, so we no-op. In production
  // the overlay is always mounted inside the router.
  if (!useRouter({ warn: false })) {
    return;
  }

  // CC-19: the close is DEFERRED to a macrotask so that it runs AFTER the
  // `history.go(1)` that the library executes when blocking BACK. Thus a `close()`
  // that navigates (overlays addressable by URL such as the parental gate `?gate=1`,
  // which toggles the search-param with `replace:true`) does not compete with that restoration
  // and does not leave URL/state desynchronized (overlay bouncing open on reload).
  // For local-signal overlays the deferral is imperceptible.
  const deferredClose = () => {
    setTimeout(close, 0);
  };
  const entry: OverlayEntry = { isOpen, close: deferredClose };
  stack.push(entry);
  onCleanup(() => {
    const idx = stack.indexOf(entry);
    if (idx !== -1) {
      stack.splice(idx, 1);
    }
  });

  // `enableBeforeUnload: false` → we do not want the native "leave site?" prompt.
  useBlocker({
    enableBeforeUnload: false,
    shouldBlockFn: ({ action }) => resolveBackDismiss(action, entry),
  });
}
